/**
 * Tag filtering for the blog listing.
 *
 * Supports multi-select OR filtering: several category pills can be active at
 * once, and results include articles tagged with any selected category.
 * "All Articles" is active when no tags are selected; selecting a tag clears it.
 *
 * Single-tag requests use the Section Rendering API. Multi-tag requests fetch
 * each tag in parallel, merge unique articles, and sort by publish date.
 *
 * Shopify returns 404 for /blogs/:handle/tagged/:tag when no articles use that
 * tag. Empty or broken single-tag responses show the empty-state notice plus
 * recent blogs. Multi-tag selections only fall back when every tag is empty.
 */
if (!customElements.get('blog-filter')) {
  class BlogFilter extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.blog-filter__list');
      this.prevBtn = this.querySelector('.blog-filter__arrow--prev');
      this.nextBtn = this.querySelector('.blog-filter__arrow--next');
      this.activeTags = new Set();
      this.controller = null;

      const stringsEl = this.querySelector('[data-blog-filter-strings]');
      try {
        this.strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};
      } catch {
        this.strings = {};
      }

      this.addEventListener('click', this.handleClick.bind(this));

      if (this.list) {
        this.updateArrows = this.updateArrows.bind(this);
        this.list.addEventListener('scroll', this.updateArrows, { passive: true });

        this.resizeObserver = new ResizeObserver(this.updateArrows);
        this.resizeObserver.observe(this.list);

        this.updateArrows();
      }
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.list) this.list.removeEventListener('scroll', this.updateArrows);
    }

    updateArrows() {
      if (!this.list || !this.prevBtn || !this.nextBtn) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.list;
      const overflows = scrollWidth > clientWidth + 1;

      this.prevBtn.hidden = !overflows;
      this.nextBtn.hidden = !overflows;

      if (!overflows) return;

      this.prevBtn.disabled = scrollLeft <= 1;
      this.nextBtn.disabled = scrollLeft >= scrollWidth - clientWidth - 1;
    }

    scrollByPage(direction) {
      if (!this.list) return;
      this.list.scrollBy({ left: direction * this.list.clientWidth * 0.8, behavior: 'smooth' });
    }

    get resultsEl() {
      return document.querySelector(this.dataset.results || '#blog-results-wrapper');
    }

    get resultsSelector() {
      return this.dataset.results || '#blog-results-wrapper';
    }

    get allPill() {
      return this.querySelector('.blog-filter__pill[data-tag=""]');
    }

    handleClick(evt) {
      const arrow = evt.target.closest('.blog-filter__arrow');
      if (arrow) {
        this.scrollByPage(arrow.classList.contains('blog-filter__arrow--next') ? 1 : -1);
        return;
      }

      const pill = evt.target.closest('.blog-filter__pill');
      if (!pill) return;

      const tag = pill.dataset.tag || '';

      if (tag === '') {
        this.activeTags.clear();
      } else if (this.activeTags.has(tag)) {
        this.activeTags.delete(tag);
      } else {
        this.activeTags.add(tag);
      }

      this.updatePillStates();
      this.load();
    }

    updatePillStates() {
      const hasTags = this.activeTags.size > 0;

      this.querySelectorAll('.blog-filter__pill').forEach((el) => {
        const pillTag = el.dataset.tag || '';
        const isActive = pillTag === '' ? !hasTags : this.activeTags.has(pillTag);
        el.classList.toggle('is-active', isActive);
        el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    sectionUrl(pill) {
      const url = new URL(pill.dataset.url || window.location.pathname, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      return url.toString();
    }

    unwrapDeferred(root) {
      root.querySelectorAll('deferred-section').forEach((deferred) => {
        const innerTpl = deferred.querySelector(':scope > template');
        if (innerTpl) {
          deferred.parentNode.insertBefore(innerTpl.content, deferred);
          deferred.remove();
        }
      });
    }

    parseResponse(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.unwrapDeferred(doc);
      return doc;
    }

    pillLabel(pill) {
      return pill.querySelector('span')?.textContent.trim() || pill.dataset.tag || '';
    }

    pillForTag(tag) {
      return this.querySelector(`.blog-filter__pill[data-tag="${tag}"]`);
    }

    emptyNoticeHtml(label) {
      const template = this.strings.emptyForTag
        || "Sorry! We couldn't find blogs tagged as '@@TAG@@'. Showing our <strong>Recent Blogs</strong>";
      return template.replace('@@TAG@@', label);
    }

    resultsFromDoc(doc) {
      return doc.querySelector(this.resultsSelector);
    }

    resultCount(node) {
      return node ? node.querySelectorAll('.js-pagination-result').length : 0;
    }

    responseIsFallback(fresh) {
      return Boolean(fresh?.querySelector('.blog-results-fallback'));
    }

    responseIsBroken(html, fresh) {
      return !fresh || /Liquid error/i.test(html) || /Liquid error/i.test(fresh.textContent || '');
    }

    articleKey(item) {
      return item.dataset.articleUrl
        || item.querySelector('a[href*="/blogs/"]')?.pathname
        || item.querySelector('.card-link')?.pathname
        || '';
    }

    publishedAt(item) {
      const stamp = parseInt(item.dataset.publishedAt, 10);
      if (stamp) return stamp;
      const time = item.querySelector('time[datetime]');
      return time ? Date.parse(time.dateTime) : 0;
    }

    mergeItems(items) {
      const byUrl = new Map();

      items.forEach((item) => {
        const key = this.articleKey(item);
        if (!key || byUrl.has(key)) return;
        byUrl.set(key, item);
      });

      return [...byUrl.values()].sort((a, b) => this.publishedAt(b) - this.publishedAt(a));
    }

    extractItems(fresh) {
      return Array.from(fresh.querySelectorAll('.js-pagination-result')).map((item) => item.cloneNode(true));
    }

    nextPageUrl(fresh, currentUrl) {
      const loadMore = fresh.querySelector('.js-pagination-load-more');
      const pagination = fresh.querySelector('.js-pagination');
      if (!loadMore) return null;

      // Modern pagination renders href="" on the last page; the DOM resolves that
      // to the current document URL, which would otherwise loop forever.
      const rawHref = loadMore.getAttribute('href');
      if (!rawHref || loadMore.getAttribute('aria-disabled') === 'true') return null;
      if (pagination && pagination.dataset.isMoreResults !== 'true') return null;

      const url = new URL(rawHref, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const next = url.toString();
      if (currentUrl && next === currentUrl) return null;
      return next;
    }

    async fetchResults(url, signal) {
      const response = await fetch(url, { signal });
      const html = await response.text();
      const doc = this.parseResponse(html);
      const fresh = this.resultsFromDoc(doc);
      return { response, html, fresh };
    }

    async fetchAllItemsForPill(pill, signal) {
      const items = [];
      let url = this.sectionUrl(pill);
      const seenUrls = new Set();

      while (url) {
        if (seenUrls.has(url)) break;
        seenUrls.add(url);

        const { html, fresh } = await this.fetchResults(url, signal);
        if (this.responseIsBroken(html, fresh)) break;

        // Empty tags render the "recent blogs" fallback with paginated all-articles
        // markup — do not walk those pages during multi-select merges.
        if (this.responseIsFallback(fresh)) break;

        items.push(...this.extractItems(fresh));
        url = this.nextPageUrl(fresh, url);
      }

      return items;
    }

    async fetchTagItems(pill, signal) {
      try {
        const { html, fresh } = await this.fetchResults(this.sectionUrl(pill), signal);
        if (this.responseIsBroken(html, fresh)) return [];
        return this.extractItems(fresh);
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return [];
      }
    }

    applyFallback(target, fresh, label) {
      const fallback = document.createElement('div');
      fallback.className = 'blog-results-fallback';

      const notice = document.createElement('p');
      notice.className = 'blog-filter-empty-notice';
      notice.setAttribute('role', 'status');
      notice.innerHTML = this.emptyNoticeHtml(label);
      fallback.appendChild(notice);

      while (fresh.firstChild) {
        fallback.appendChild(fresh.firstChild);
      }

      target.replaceChildren(fallback);

      const blogResults = target.querySelector('blog-results');
      if (blogResults) {
        blogResults.dataset.filtered = 'false';
      }
    }

    async loadFallbackWithLabel(label, target, controller) {
      if (!this.allPill) throw new Error('[blog-filter] No "All Articles" pill found');

      const { html, fresh } = await this.fetchResults(this.sectionUrl(this.allPill), controller.signal);
      if (this.responseIsBroken(html, fresh)) {
        throw new Error('[blog-filter] Fallback request returned no usable results');
      }

      this.applyFallback(target, fresh.cloneNode(true), label);
    }

    applyMergedResults(target, fresh, merged) {
      const clone = fresh.cloneNode(true);
      const grid = clone.querySelector('#blog-results') || clone.querySelector('ul');

      if (grid) {
        grid.innerHTML = '';
        merged.forEach((item) => grid.appendChild(item));
      }

      clone.querySelector('.js-pagination')?.remove();
      clone.querySelectorAll('.js-when-paginated-only').forEach((el) => el.remove());

      const blogResults = clone.querySelector('blog-results');
      if (blogResults) {
        blogResults.dataset.total = String(merged.length);
        blogResults.dataset.filtered = 'true';
      }

      target.innerHTML = clone.innerHTML;
    }

    async loadSingle(pill, target, controller) {
      const tag = pill.dataset.tag || '';
      const { html, fresh } = await this.fetchResults(this.sectionUrl(pill), controller.signal);
      const count = this.resultCount(fresh);
      const needsFallback = tag && (this.responseIsBroken(html, fresh) || count === 0);

      if (needsFallback) {
        await this.loadFallbackWithLabel(this.pillLabel(pill), target, controller);
        return;
      }

      if (!fresh) throw new Error(`No ${this.resultsSelector} in the section response`);
      target.innerHTML = fresh.innerHTML;
    }

    async loadMultiple(tags, target, controller) {
      const pills = tags.map((tag) => this.pillForTag(tag)).filter(Boolean);
      if (!pills.length) return;

      const itemGroups = await Promise.all(pills.map((pill) => this.fetchAllItemsForPill(pill, controller.signal)));
      const merged = this.mergeItems(itemGroups.flat());

      if (merged.length === 0) {
        const label = pills.map((pill) => this.pillLabel(pill)).join(', ');
        await this.loadFallbackWithLabel(label, target, controller);
        return;
      }

      if (!this.allPill) throw new Error('[blog-filter] No "All Articles" pill found');

      const { html, fresh } = await this.fetchResults(this.sectionUrl(this.allPill), controller.signal);
      if (this.responseIsBroken(html, fresh)) {
        throw new Error('[blog-filter] Could not load results template');
      }

      this.applyMergedResults(target, fresh, merged);
    }

    async loadAll(target, controller) {
      if (!this.allPill) throw new Error('[blog-filter] No "All Articles" pill found');

      const { html, fresh } = await this.fetchResults(this.sectionUrl(this.allPill), controller.signal);
      if (!fresh || this.responseIsBroken(html, fresh)) {
        throw new Error(`No ${this.resultsSelector} in the section response`);
      }

      target.innerHTML = fresh.innerHTML;
    }

    async load() {
      const target = this.resultsEl;

      if (!target) {
        console.warn('[blog-filter] No results container matching', this.resultsSelector);
        return;
      }

      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;

      target.classList.add('is-loading');
      target.setAttribute('aria-busy', 'true');

      const tags = [...this.activeTags];

      try {
        if (tags.length === 0) {
          await this.loadAll(target, controller);
        } else if (tags.length === 1) {
          await this.loadSingle(this.pillForTag(tags[0]), target, controller);
        } else {
          await this.loadMultiple(tags, target, controller);
        }

        const finalCount = target.querySelectorAll('.js-pagination-result').length;
        const strays = document.querySelectorAll('.card--article').length - finalCount;
        if (strays > 0) {
          console.warn(
            `[blog-filter] ${strays} article card(s) render outside ${this.resultsSelector} ` +
              'and are unaffected by filtering — another section on this template is rendering them.'
          );
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[blog-filter]', error);
      } finally {
        if (this.controller === controller) {
          target.classList.remove('is-loading');
          target.removeAttribute('aria-busy');
          this.controller = null;
        }
      }
    }
  }

  customElements.define('blog-filter', BlogFilter);
}
